import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, Loader2, LogOut, Download, Eye, FileSpreadsheet,
  ShoppingBag, Users, DollarSign,
} from "lucide-react";
import SEO from "../components/seo/SEO";
import { PRODUCTS } from "../config/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mfg_admin_token";
const imgFor = (id) => PRODUCTS.find((p) => p.id === id)?.image || PRODUCTS[0]?.image;
const officeViewer = (url) => `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4">
      <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand"><Lock className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Admin sign in</h1>
            <p className="text-sm text-muted-foreground">Owner access only</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" data-testid="admin-email"
            className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" data-testid="admin-password"
            className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-action px-6 py-3 text-sm font-semibold text-white hover:bg-action-hover disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
      <p className="mt-2 font-heading text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("files");
  const [files, setFiles] = useState([]);
  const [orders, setOrders] = useState(null);
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [f, o, l] = await Promise.all([
        axios.get(`${API}/admin/files`, authHeader),
        axios.get(`${API}/admin/orders`, authHeader),
        axios.get(`${API}/admin/leads`, authHeader),
      ]);
      setFiles(f.data.files);
      setOrders(o.data);
      setLeads(l.data);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired, please sign in again"); logout(); }
      else toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  if (!token) return (<><SEO title="Admin" description="Admin" path="/admin" noindex /><Login onLogin={setToken} /></>);

  const dl = (file) => `${API}/admin/download/${file}?token=${encodeURIComponent(token)}`;

  return (
    <>
      <SEO title="Admin — MyFinancesGuru Tools" description="Admin dashboard" path="/admin" noindex />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-bold text-foreground">Admin</h1>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        {orders && (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat icon={DollarSign} label="Revenue" value={`$${orders.revenue}`} />
            <Stat icon={ShoppingBag} label="Paid orders" value={orders.paid_count} />
            <Stat icon={Users} label="Email leads" value={leads?.leads_count ?? "—"} />
            <Stat icon={Users} label="Advisor reqs" value={leads?.advisor_count ?? "—"} />
          </div>
        )}

        <div className="mt-8 flex gap-2 border-b border-border">
          {[["files", "Spreadsheets"], ["orders", "Orders"], ["leads", "Leads"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "border-brand text-brand" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>

        {loading && <div className="py-16 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-brand" /></div>}

        {!loading && tab === "files" && (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-files-grid">
            {files.map((f) => (
              <div key={f.file} className="overflow-hidden rounded-xl border border-border bg-card" data-testid={`admin-file-${f.id}`}>
                <div className="aspect-[3/2] overflow-hidden bg-muted">
                  <img src={imgFor(f.id)} alt={f.name} className="h-full w-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-base font-semibold text-foreground">{f.name}</h3>
                    <span className="shrink-0 rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold text-brand">{f.price ? `$${f.price}` : "Free"}</span>
                  </div>
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><FileSpreadsheet className="h-3 w-3" /> {f.file} · {f.kb} KB</p>
                  <div className="mt-4 flex gap-2">
                    <a href={dl(f.file)} data-testid={`admin-download-${f.id}`}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-action px-3 py-2 text-xs font-semibold text-white hover:bg-action-hover">
                      <Download className="h-3.5 w-3.5" /> Download
                    </a>
                    {f.file.endsWith(".xlsx") && (
                      <a href={officeViewer(dl(f.file))} target="_blank" rel="noreferrer" data-testid={`admin-preview-${f.id}`}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-brand px-3 py-2 text-xs font-semibold text-brand hover:bg-brand/5">
                        <Eye className="h-3.5 w-3.5" /> Live preview
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "orders" && orders && (
          <div className="mt-6 overflow-x-auto rounded-xl border border-border" data-testid="admin-orders-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted text-xs uppercase text-muted-foreground">
                <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Email</th></tr>
              </thead>
              <tbody>
                {orders.orders.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No orders yet</td></tr>}
                {orders.orders.map((o) => (
                  <tr key={o.session_id} className="border-t border-border">
                    <td className="px-4 py-3 text-muted-foreground">{(o.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{o.lookup_key}</td>
                    <td className="px-4 py-3">${((o.amount_total ?? o.amount ?? 0) / 100).toFixed(2)} {(o.currency || "cad").toUpperCase()}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${o.payment_status === "paid" ? "bg-brand/10 text-brand" : "bg-muted text-muted-foreground"}`}>{o.payment_status}</span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{o.customer_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && tab === "leads" && leads && (
          <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="admin-leads">
            <div className="rounded-xl border border-border">
              <h3 className="border-b border-border px-4 py-3 font-heading font-semibold text-foreground">Email signups ({leads.leads_count})</h3>
              <ul className="divide-y divide-border">
                {leads.leads.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No signups yet</li>}
                {leads.leads.map((l, i) => (
                  <li key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                    <span className="font-medium text-foreground">{l.email}</span>
                    <span className="text-xs text-muted-foreground">{l.source || ""} · {(l.created_at || "").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-border">
              <h3 className="border-b border-border px-4 py-3 font-heading font-semibold text-foreground">Advisor requests ({leads.advisor_count})</h3>
              <ul className="divide-y divide-border">
                {leads.advisor_requests.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No requests yet</li>}
                {leads.advisor_requests.map((a, i) => (
                  <li key={i} className="px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">{a.name || a.email}</span>
                      <span className="text-xs text-muted-foreground">{(a.created_at || "").slice(0, 10)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{a.email}{a.timeline ? ` · ${a.timeline}` : ""}{a.assets ? ` · ${a.assets}` : ""}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
